import BaseTrackingService from '../BaseTrackingService';
import { handleAxiosErrorMessage } from '../customErrors/apiErrors';
import { EVENTS } from './trackEvents';

/**
 * @typedef {Object} PaymentData
 * @property {string} id - ID do pagamento
 * @property {string} transaction_id - ID da transação
 * @property {number} amount - Valor do pagamento
 * @property {string} status - Status do pagamento
 */
/**
 * @typedef {Object} PaymentMethod
 * @property {string} [type] - Tipo do método de pagamento
 * @property {string} [provider] - Provedor do pagamento
 * @property {string} [card_number] - Número primeiros números do cartão
 * @property {string} [card_brand] - Bandeira do cartão
 * @property {Object} [info] - Informações adicionais
 */

class UpgradeTrackingService extends BaseTrackingService {
  // Mantido como literal (não referencia EVENTS.UPGRADE_STARTED): o event_type
  // deste payload já diverge do evento realmente disparado ('upgrade_started'),
  // divergência conhecida e mantida por ora — ver auditoria de duplicidade.
  static EVENT_UPGRADE_STARTED = 'limit_reach_upgrade_button_clicked';
  static EVENT_UPGRADE_PAYMENT_UPGRADED = EVENTS.PLAN_UPGRADED;
  static EVENT_UPGRADE_PAYMENT_SUCCESS = EVENTS.UPGRADE_PAYMENT_SUCCESS;
  static EVENT_UPGRADE_PAYMENT_FAILED = EVENTS.UPGRADE_PAYMENT_FAILED;
  static EVENT_UPGRADE_CARD_TOKENIZATION_SUCCESS =
    EVENTS.UPGRADE_CARD_TOKENIZATION_SUCCESS;
  static EVENT_UPGRADE_CARD_TOKENIZATION_ERROR =
    EVENTS.UPGRADE_CARD_TOKENIZATION_ERROR;

  constructor() {
    super('UpgradeTrackingService');
  }

  /**
   * Monta propriedades para `trackUpgradePaymentSuccess` em trackEvents.ts.
   * @param {Object} userData - Dados do usuário
   * @param {Object} currentPlan - Plano atual
   * @param {Object} newPlan - Novo plano
   * @param {PaymentData} paymentData - Dados do pagamento
   * @param {PaymentMethod} paymentMethod - Método de pagamento
   * @param {string} [context] - Contexto da rota / fluxo
   * @returns {Object} Propriedades a enviar em `additionalProperties`
   */
  prepareUpgradePaymentSuccessForAnalytics(
    userData,
    currentPlan,
    newPlan,
    paymentData,
    paymentMethod = {},
    context,
  ) {
    return this._buildUpgradePaymentSuccessTrackingData(
      userData,
      currentPlan,
      newPlan,
      paymentData,
      paymentMethod,
      context,
    );
  }

  /**
   * @param {Object} userData - Dados do usuário
   * @param {Object} currentPlan - Plano atual
   * @param {Object} newPlan - Novo plano
   * @param {PaymentData} paymentData - Dados do pagamento
   * @param {PaymentMethod} paymentMethod - Método de pagamento
   * @param {string} [context] - Contexto da rota / fluxo
   */
  _buildUpgradePaymentSuccessTrackingData(
    userData,
    currentPlan,
    newPlan,
    paymentData,
    paymentMethod = {},
    context,
  ) {
    const currentPlanAmount =
      currentPlan?.plan_details?.amount || currentPlan?.price || 0;
    const newPlanAmount = newPlan?.amount || newPlan?.price || 0;
    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),

      // Plano atual
      current_plan_id: currentPlan?.plan_details?.id,
      current_plan_name: currentPlan?.plan_details?.name,
      current_plan_amount: currentPlanAmount,
      current_plan_price: currentPlanAmount,
      current_plan_type: currentPlan?.plan_details?.type,

      // Novo plano
      new_plan_id: newPlan.id,
      new_plan_name: newPlan.name,
      new_plan_amount: newPlanAmount,
      new_plan_price: newPlanAmount,
      new_plan_type: newPlan.type,

      // Diferença de valor
      upgrade_amount: (newPlanAmount ?? 0) - (currentPlanAmount ?? 0),
      upgrade_percentage:
        currentPlanAmount > 0
          ? (((newPlanAmount ?? 0) - (currentPlanAmount ?? 0)) /
              (currentPlanAmount ?? 0)) *
            100
          : null,

      // Dados do pagamento
      payment_id: paymentData.id || paymentData.transaction_id,
      payment_amount: paymentData.amount || newPlanAmount - currentPlanAmount,
      payment_status: paymentData.status || 'success',
      provider:
        paymentMethod?.provider ||
        paymentMethod?.info?.provider ||
        newPlan?.provider ||
        newPlan?.subscription_status?.info?.provider ||
        currentPlan?.info?.provider ||
        'unknown',

      // Método de pagamento
      payment_method: paymentMethod.type || 'card',
      card_number_masked: this._maskCardNumber(paymentMethod.card_number),
      card_brand: this._getCardBrand(paymentMethod.card_number),

      // Metadados da operação
      context: context || 'upgrade',
      timestamp: new Date().toISOString(),
      event_type: UpgradeTrackingService.EVENT_UPGRADE_PAYMENT_SUCCESS,
      conversion_value: 1,
      upgrade_step: 'completed',
      status: 'success',
    };
  }

  /**
   * Monta propriedades para `trackUpgradeStarted` em trackEvents.ts.
   * @param {Object} userData - Dados do usuário
   * @param {Object} currentPlan - Plano atual
   * @param {Object} newPlan - Novo plano
   * @param {Object} paymentMethod - Método de pagamento
   * @param {string} [context] - Contexto da rota / fluxo
   * @returns {Object} Propriedades a enviar em `additionalProperties`
   */
  prepareUpgradeStartedForAnalytics(
    userData,
    currentPlan,
    newPlan,
    paymentMethod = {},
    context,
  ) {
    return this._buildUpgradeStartedTrackingData(
      userData,
      currentPlan,
      newPlan,
      paymentMethod,
      context,
    );
  }

  /**
   * @param {Object} userData - Dados do usuário
   * @param {Object} currentPlan - Plano atual
   * @param {Object} newPlan - Novo plano
   * @param {Object} paymentMethod - Método de pagamento
   * @param {string} [context] - Contexto da rota / fluxo
   */
  _buildUpgradeStartedTrackingData(
    userData,
    currentPlan,
    newPlan,
    paymentMethod = {},
    context,
  ) {
    const currentPlanAmount =
      currentPlan?.plan_details?.amount || currentPlan?.price || 0;
    const newPlanAmount = newPlan?.amount || newPlan?.price || 0;
    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),

      // Plano atual
      current_plan_id: currentPlan?.plan_details?.id,
      current_plan_name: currentPlan?.plan_details?.name,
      current_plan_amount: currentPlanAmount,
      current_plan_price: currentPlanAmount,
      current_plan_type: currentPlan?.plan_details?.type,

      // Novo plano
      new_plan_id: newPlan.id,
      new_plan_name: newPlan.name,
      new_plan_amount: newPlanAmount,
      new_plan_price: newPlanAmount,
      new_plan_type: newPlan.type,

      // Diferença de valor
      upgrade_amount: (newPlanAmount ?? 0) - (currentPlanAmount ?? 0),
      upgrade_percentage:
        currentPlanAmount > 0
          ? (((newPlanAmount ?? 0) - (currentPlanAmount ?? 0)) /
              currentPlanAmount) *
            100
          : null,

      // Método de pagamento
      payment_method: paymentMethod?.type || 'unknown',
      payment_provider: paymentMethod?.provider || 'unknown',
      card_number_masked: this._maskCardNumber(paymentMethod?.card_number),
      card_brand: this._getCardBrand(paymentMethod?.card_number),
      // provider: paymentMethod?.provider || 'unknown',
      provider:
        paymentMethod?.provider ||
        paymentMethod?.info?.provider ||
        newPlan?.provider ||
        currentPlan?.info?.provider ||
        'unknown',

      // Metadados da operação
      context: context || 'upgrade',
      timestamp: new Date().toISOString(),
      event_type: UpgradeTrackingService.EVENT_UPGRADE_STARTED,
      conversion_value: 0,
      upgrade_step: 'initiated',
    };
  }

  /**
   * Monta propriedades para `trackPlanUpgraded` em trackEvents.ts.
   * @param {Object} userData - Dados do usuário
   * @param {Object} currentPlan - Plano atual
   * @param {Object} newPlan - Novo plano
   * @param {PaymentMethod} paymentMethod - Método de pagamento
   * @param {string} [context] - Contexto da rota / fluxo
   * @returns {Object} Propriedades a enviar em `additionalProperties`
   */
  preparePlanUpgradedForAnalytics(
    userData,
    currentPlan,
    newPlan,
    paymentMethod = {},
    context,
  ) {
    return this._buildPlanUpgradedTrackingData(
      userData,
      currentPlan,
      newPlan,
      paymentMethod,
      context,
    );
  }

  /**
   * @param {Object} userData - Dados do usuário
   * @param {Object} currentPlan - Plano atual
   * @param {Object} newPlan - Novo plano
   * @param {PaymentMethod} paymentMethod - Método de pagamento
   * @param {string} [context] - Contexto da rota / fluxo
   */
  _buildPlanUpgradedTrackingData(
    userData,
    currentPlan,
    newPlan,
    paymentMethod = {},
    context,
  ) {
    const currentPlanAmount =
      currentPlan?.plan_details?.amount || currentPlan?.price || 0;
    const newPlanAmount = newPlan?.amount || newPlan?.price || 0;
    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),

      // Plano atual
      current_plan_id: currentPlan?.plan_details?.id,
      current_plan_name: currentPlan?.plan_details?.name,
      current_plan_amount: currentPlanAmount,
      current_plan_price: currentPlanAmount,
      current_plan_type: currentPlan?.plan_details?.type,

      // Novo plano
      new_plan_id: newPlan.id,
      new_plan_name: newPlan.name,
      new_plan_amount: newPlanAmount,
      new_plan_price: newPlanAmount,
      new_plan_type: newPlan.type,

      // Diferença de valor
      upgrade_amount: (newPlanAmount ?? 0) - (currentPlanAmount ?? 0),
      upgrade_percentage:
        currentPlanAmount > 0
          ? (((newPlanAmount ?? 0) - (currentPlanAmount ?? 0)) /
              currentPlanAmount) *
            100
          : null,

      // Método de pagamento
      payment_method: paymentMethod?.type || 'unknown',
      payment_provider: paymentMethod?.provider || 'unknown',
      card_number_masked: this._maskCardNumber(paymentMethod?.card_number),
      card_brand: this._getCardBrand(paymentMethod?.card_number),
      // provider: paymentMethod?.provider || 'unknown',
      provider:
        paymentMethod?.provider ||
        paymentMethod?.info?.provider ||
        newPlan?.provider ||
        currentPlan?.info?.provider ||
        'unknown',

      // Metadados da operação
      context: context || 'upgrade',
      timestamp: new Date().toISOString(),
      event_type: UpgradeTrackingService.EVENT_UPGRADE_PAYMENT_UPGRADED,
      conversion_value: 0,
      upgrade_step: 'confirmed',
    };
  }

  /**
   * Monta propriedades para `trackUpgradePaymentFailed` em trackEvents.ts.
   */
  prepareUpgradePaymentFailedForAnalytics(
    userData,
    currentPlan,
    newPlan,
    error,
    paymentMethod = {},
    context,
  ) {
    return this._buildUpgradePaymentFailedTrackingData(
      userData,
      currentPlan,
      newPlan,
      error,
      paymentMethod,
      context,
    );
  }

  /**
   * @param {Object} userData - Dados do usuário
   * @param {Object} currentPlan - Plano atual
   * @param {Object} newPlan - Novo plano
   * @param {Object} error - Dados do erro
   * @param {PaymentMethod} paymentMethod - Método de pagamento
   */
  _buildUpgradePaymentFailedTrackingData(
    userData,
    currentPlan,
    newPlan,
    error,
    paymentMethod = {},
    context,
  ) {
    const {
      message = null,
      data = null,
      status = null,
    } = handleAxiosErrorMessage(error);
    const currentPlanAmount =
      currentPlan?.plan_details?.amount || currentPlan?.price || 0;
    const newPlanAmount = newPlan?.amount || newPlan?.price || 0;
    const trackingData = {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),

      // Plano atual
      current_plan_id: currentPlan?.plan_details?.id,
      current_plan_name: currentPlan?.plan_details?.name,
      current_plan_amount: currentPlanAmount,
      current_plan_price: currentPlanAmount,
      current_plan_type: currentPlan?.plan_details?.type,

      // Novo plano
      new_plan_id: newPlan.id,
      new_plan_name: newPlan.name,
      new_plan_amount: newPlanAmount,
      new_plan_price: newPlanAmount,
      new_plan_type: newPlan.type,

      // Diferença de valor
      upgrade_amount: (newPlanAmount ?? 0) - (currentPlanAmount ?? 0),
      upgrade_percentage:
        currentPlanAmount > 0
          ? (((newPlanAmount ?? 0) - (currentPlanAmount ?? 0)) /
              currentPlanAmount) *
            100
          : null,

      // Dados do erro
      error_message: message || error?.message || 'Unknown upgrade error',
      error_code: status || error?.code || 'UNKNOWN_ERROR',
      error_type: error?.type || 'upgrade_payment_failed',
      error_details: data || error?.details || 'No details available',
      failure_reason: error?.failure_reason || 'payment_failed',
      error_provider: error?.provider,

      // Método de pagamento
      payment_method: paymentMethod.type || 'card',
      card_number_masked: this._maskCardNumber(paymentMethod.card_number),
      card_brand: this._getCardBrand(paymentMethod.card_number),
      provider:
        paymentMethod?.info?.provider ||
        newPlan?.provider ||
        currentPlan?.info?.provider ||
        'unknown',

      // Metadados da operação
      context: context || 'upgrade',
      timestamp: new Date().toISOString(),
      event_type: UpgradeTrackingService.EVENT_UPGRADE_PAYMENT_FAILED,
      conversion_value: 0,
      upgrade_step: 'failed',
      status: 'error',
    };

    return trackingData;
  }

  /**
   * Monta propriedades para `trackUpgradeCardTokenizationSuccess` em trackEvents.ts.
   * @param {Object} userData - Dados do usuário
   * @param {Object} currentPlan - Plano atual
   * @param {Object} newPlan - Novo plano
   * @param {Object} tokenData - Dados do token gerado
   * @param {Object} cardData - Dados do cartão
   * @param {string} [context] - Contexto da rota / fluxo
   * @returns {Object} Propriedades a enviar em `additionalProperties`
   */
  prepareUpgradeCardTokenizationSuccessForAnalytics(
    userData,
    currentPlan,
    newPlan,
    tokenData,
    cardData,
    context,
  ) {
    return this._buildUpgradeCardTokenizationSuccessTrackingData(
      userData,
      currentPlan,
      newPlan,
      tokenData,
      cardData,
      context,
    );
  }

  /**
   * @param {Object} userData - Dados do usuário
   * @param {Object} currentPlan - Plano atual
   * @param {Object} newPlan - Novo plano
   * @param {Object} tokenData - Dados do token gerado
   * @param {Object} cardData - Dados do cartão
   * @param {string} [context] - Contexto da rota / fluxo
   */
  _buildUpgradeCardTokenizationSuccessTrackingData(
    userData,
    currentPlan,
    newPlan,
    tokenData,
    cardData,
    context,
  ) {
    const newPlanAmount = newPlan?.amount || newPlan?.price || 0;
    const currentPlanAmount =
      currentPlan?.plan_details?.amount || currentPlan?.price || 0;

    return {
      // Novo plano
      new_plan_id: newPlan.id,
      new_plan_name: newPlan.name,
      new_plan_amount: newPlanAmount,
      new_plan_price: newPlanAmount,
      new_plan_type: newPlan.type,

      // Diferença de valor
      upgrade_amount: (newPlanAmount ?? 0) - (currentPlanAmount ?? 0),
      upgrade_percentage:
        currentPlanAmount > 0
          ? (((newPlanAmount ?? 0) - (currentPlanAmount ?? 0)) /
              currentPlanAmount) *
            100
          : null,

      // Informações do cartão
      card_hash: tokenData.id || tokenData.card_hash,
      card_number_masked: this._maskCardNumber(cardData.card_number),
      card_brand: this._getCardBrand(cardData.card_number),
      // provider: userData?.provider || 'unknown',
      provider:
        tokenData?.info?.provider ||
        newPlan?.provider ||
        currentPlan?.info?.provider ||
        'unknown',

      // Metadados da operação
      context: context || 'upgrade',
      timestamp: new Date().toISOString(),
      status: 'success',
      event_type:
        UpgradeTrackingService.EVENT_UPGRADE_CARD_TOKENIZATION_SUCCESS,
      conversion_value: 1,
      upgrade_step: 'card_tokenized',
    };
  }

  /**
   * Monta propriedades para `trackUpgradeCardTokenizationError` em trackEvents.ts.
   * @param {Object} userData - Dados do usuário
   * @param {Object} currentPlan - Plano atual
   * @param {Object} newPlan - Novo plano
   * @param {Object} error - Erro ocorrido
   * @param {Object} cardData - Dados do cartão
   * @param {string} [context] - Contexto da rota / fluxo
   * @returns {Object} Propriedades a enviar em `additionalProperties`
   */
  prepareUpgradeCardTokenizationErrorForAnalytics(
    userData,
    currentPlan,
    newPlan,
    error,
    cardData,
    context,
  ) {
    return this._buildUpgradeCardTokenizationErrorTrackingData(
      userData,
      currentPlan,
      newPlan,
      error,
      cardData,
      context,
    );
  }

  /**
   * @param {Object} userData - Dados do usuário
   * @param {Object} currentPlan - Plano atual
   * @param {Object} newPlan - Novo plano
   * @param {Object} error - Erro ocorrido
   * @param {Object} cardData - Dados do cartão
   */
  _buildUpgradeCardTokenizationErrorTrackingData(
    userData,
    currentPlan,
    newPlan,
    error,
    cardData,
    context,
  ) {
    const newPlanAmount = newPlan?.amount || newPlan?.price || 0;
    const currentPlanAmount =
      currentPlan?.plan_details?.amount || currentPlan?.price || 0;

    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),

      // Plano atual
      current_plan_id: currentPlan?.plan_details?.id,
      current_plan_name: currentPlan?.plan_details?.name,
      current_plan_amount: currentPlanAmount,
      current_plan_price: currentPlanAmount,
      current_plan_type: currentPlan?.plan_details?.type,

      // Novo plano
      new_plan_id: newPlan.id,
      new_plan_name: newPlan.name,
      new_plan_amount: newPlanAmount,
      new_plan_price: newPlanAmount,
      new_plan_type: newPlan.type,

      // Diferença de valor
      upgrade_amount: (newPlanAmount ?? 0) - (currentPlanAmount ?? 0),
      upgrade_percentage:
        currentPlanAmount > 0
          ? (((newPlanAmount ?? 0) - (currentPlanAmount ?? 0)) /
              currentPlanAmount) *
            100
          : null,

      // Informações do cartão (mascaradas por segurança)
      card_number_masked: this._maskCardNumber(cardData.card_number),
      card_brand: this._getCardBrand(cardData.card_number),

      // Informações do erro
      error_message: error?.message || 'Unknown error',
      error_code: error?.code || 'UNKNOWN_ERROR',
      error_type: error?.type || 'tokenization_error',
      provider: currentPlan?.info?.provider || 'unknown',

      // Metadados da operação
      context: context || 'upgrade',
      timestamp: new Date().toISOString(),
      status: 'error',
      event_type: UpgradeTrackingService.EVENT_UPGRADE_CARD_TOKENIZATION_ERROR,
      conversion_value: 0,
      upgrade_step: 'card_tokenization_failed',
    };
  }
}

export default new UpgradeTrackingService();
